//@flow
import _ from 'lodash';
import { getDayDiff } from './index';
import {
  ENTERPRISE,
  EXPIRED_STATUS,
  EXCEEDED_STATUS,
  PACKAGE_NAME01,
  PACKAGE_NAME02,
  PACKAGE_NAME03,
  SUBSCRIBED_STATUS,
  TRIAL_STATUS,
  REVERSE_TRIAL_RELEASE_DATE
} from 'src/constants/subscription-v5';
import moment from 'moment';
import { DATE_FORMAT } from 'src/constants/language-setting';

// Get current package type by name
export const getCurrentPackageType = (name = '') => {
  if (name.toLocaleLowerCase().includes(PACKAGE_NAME01)) return PACKAGE_NAME01;
  if (name.toLocaleLowerCase().includes(PACKAGE_NAME02)) return PACKAGE_NAME02;
  if (name.toLocaleLowerCase().includes(PACKAGE_NAME03)) return PACKAGE_NAME03;
  if (name.toLocaleLowerCase().includes(ENTERPRISE)) return ENTERPRISE;
  return PACKAGE_NAME01;
};

const startsWithAnyPlan = (name = '', plans = []) => {
  const lower = name.toLocaleLowerCase();
  return plans.some(plan => lower.startsWith(plan));
};

export const checkReverseTrialLockedFeature = (tenantApp, settingPath, defaultValue = false) => {
  const enabledSubscriptionV5 = _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false);
  const isFeatureLocked = !_.get(tenantApp, settingPath, defaultValue || false);
  const isValidReverseTrialDate =
    _.get(tenantApp, 'recentInstalledAt', 0) >= REVERSE_TRIAL_RELEASE_DATE;
  return enabledSubscriptionV5 && isFeatureLocked && isValidReverseTrialDate;
};

export const checkReverseTrialUnlockedFeature = (
  tenantApp,
  settingPath,
  defaultValue = false
) => {
  const enabledSubscriptionV5 = _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false);
  const isFeatureUnlocked = _.get(tenantApp, settingPath, defaultValue || false);
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const trialExpired = _.get(tenantApp, 'trialExpired', false);
  const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
  const trialPlan = _.get(tenantApp, 'settings.subscriptionV5.settings.trial-plan', '');
  const isValidReverseTrialDate =
    _.get(tenantApp, 'recentInstalledAt', 0) >= REVERSE_TRIAL_RELEASE_DATE;

  // Check trial expiration by timestamp (more reliable than backend flag)
  const isTrialExpiredByTimestamp = Math.floor(Date.now() / 1000) >= trialExpiresAt;
  const hasTrialEnded = trialExpired || isTrialExpiredByTimestamp;

  // For Convert plans, only show premium tag on specific features (personalization, variantsDisplay)
  const featureName = settingPath.split('.')[1] || '';
  const isLaunchPlan = selectedPlan.startsWith('launch_');
  const isConvertPlan = selectedPlan.startsWith('convert_') || trialPlan.startsWith('convert_');

  if (isConvertPlan) {
    // Convert plans: only show on allowed features
    const allowedConvertFeatures = ['personalization', 'variantsDisplay'];
    const isAllowedFeature = allowedConvertFeatures.includes(featureName);
    return (
      enabledSubscriptionV5 &&
      isFeatureUnlocked &&
      isValidReverseTrialDate &&
      !hasTrialEnded &&
      isAllowedFeature
    );
  }

  // Launch plans: show on all unlocked features
  return (
    enabledSubscriptionV5 &&
    isFeatureUnlocked &&
    isValidReverseTrialDate &&
    !hasTrialEnded &&
    isLaunchPlan
  );
};

export const checkOldLockedFeature = (tenantApp, settingPath, defaultValue = false) => {
  const isFeatureLocked = !_.get(tenantApp, settingPath, defaultValue || false);
  return isFeatureLocked;
};

// update 1: check locked feature during reverse trial
export const checkLockedFeatureDuringReverseTrial = (
  tenantApp,
  settingPath,
  defaultValue = false
) => {
  const isReverseTrialLockedFeature = checkReverseTrialLockedFeature(
    tenantApp,
    settingPath,
    defaultValue
  );
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const trialPlan = _.get(tenantApp, 'settings.subscriptionV5.settings.trial-plan', '');
  const isLaunchTrialPlan = trialPlan.startsWith('launch_');
  return isReverseTrialLockedFeature && _.isEmpty(selectedPlan) && isLaunchTrialPlan;
};

// Update 2
export const checkSubscribedDuringReverseTrial = (tenantApp, requiredPlan) => {
  const enabledSubscriptionV5 = _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false);
  const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
  const isValidReverseTrialDate =
    _.get(tenantApp, 'recentInstalledAt', 0) >= REVERSE_TRIAL_RELEASE_DATE;
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');

  // Check trial expiration by timestamp (more reliable than backend flag)
  const now = Math.floor(Date.now() / 1000);

  return (
    enabledSubscriptionV5 &&
    trialExpiresAt > now &&
    isValidReverseTrialDate &&
    startsWithAnyPlan(selectedPlan, requiredPlan)
  );
};

//update 3
export const checkLockedFeatureAfterReverseTrial = (
  tenantApp,
  settingPath,
  defaultValue = false,
  requiredPlan = []
) => {
  const isReverseTrialLockedFeature = checkReverseTrialLockedFeature(
    tenantApp,
    settingPath,
    defaultValue
  );
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);

  // Check trial expiration by timestamp (more reliable than backend flag)
  const now = Math.floor(Date.now() / 1000);
  const hasTrialEnded = trialExpiresAt <= now;

  // If requiredPlan specified, verify it matches selected plan
  const hasPlanMatch = requiredPlan.length === 0 || startsWithAnyPlan(selectedPlan, requiredPlan);

  return (
    isReverseTrialLockedFeature &&
    !_.isEmpty(selectedPlan) &&
    hasTrialEnded &&
    hasPlanMatch
  );
};

// Format gmv label: $1K, $1M, $1B
export const formatGmvLabel = v => {
  if (v === null) return 'Unlimited';

  const BILLION = 10 ** 9;
  const MILLION = 10 ** 6;
  const THOUSAND = 10 ** 3;

  const num = Number(v);
  if (!Number.isFinite(num) || num < 0) return '$0';

  const fmt = n => (Number.isInteger(n) ? n.toString() : parseFloat(n.toFixed(1)).toString());
  if (num >= BILLION) return '$' + fmt(num / BILLION) + 'B';
  if (num >= MILLION) return '$' + fmt(num / MILLION) + 'M';
  if (num >= THOUSAND) return '$' + fmt(num / THOUSAND) + 'K';
  return '$' + num.toString();
};

// Extract all tier values from all plans
export const getAllTierValues = packages => {
  const tierValues = [];

  packages.forEach(packageData => {
    if (packageData.subTiers && Array.isArray(packageData.subTiers)) {
      packageData.subTiers.forEach(tier => {
        const gmvAmount = tier.gmvMax;
        if (gmvAmount > 0 || gmvAmount === null) {
          tierValues.push({
            value: gmvAmount ?? tier.gmvMin,
            label: formatGmvLabel(gmvAmount),
            pricing: tier.pricing,
            mainTier: tier.mainTier,
            planId: tier.planId
          });
        }
      });
    }
  });

  return tierValues.sort((a, b) => (a.value === null ? 1 : a.value - b.value));
};

// Get correct tier by gmvLimit and list of tiers to view in package card
export const getCorrectTierToViewInList = (gmvLimit = 50000, tiers = []) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;

  // If gmvLimit is less than the first tier value, return the first tier
  if (gmvLimit <= tiers[0].gmvMax) return tiers[0];

  // If gmvLimit is greater than or equal to the last tier value, return the last tier
  if (gmvLimit >= tiers[tiers.length - 1].gmvMin) {
    return tiers[tiers.length - 1];
  }

  // Otherwise, find the correct tier in between
  for (let i = 0; i < tiers.length - 1; i++) {
    if (gmvLimit >= tiers[i].gmvMin && gmvLimit <= tiers[i].gmvMax) {
      return tiers[i];
    }
  }

  return null; // Fallback, should not reach here
};

export const getCurrentGMVPlan = (packagePlans: Array<Object> = [], baseGMV: Object = {}) => {
  const gmv = _.get(baseGMV, 'gmv');
  if (gmv === undefined || gmv === null) return null;

  for (const plan of packagePlans || []) {
    const found = (plan.subTiers || []).find(
      tier => gmv >= tier.gmvMin && (tier.gmvMax === null || gmv <= tier.gmvMax)
    );
    if (found) return found;
  }
  return null;
};

// Get correct layout package to display by current store GMV limit
export const getCorrectLayoutKey = (currentBaseGMVFee, selectedPlan, acceleratePackage) => {
  const accelerateMaxTier = _.get(acceleratePackage, 'subTiers[0].gmvMin', 0);
  // if selectedPlan is enterprise => 'enterprise_only'
  if (selectedPlan.toLocaleLowerCase().includes('enterprise')) return 'enterprise_only';
  // if currentBaseGMVFee is equal than min of accelerate tiers => 'accelerate_package'
  if (acceleratePackage && currentBaseGMVFee >= accelerateMaxTier) return 'accelerate_package';
  // else three_packages
  return 'three_packages';
};

/**
 * @param {*} invoice // current invoice subscribed of store
 * @param {*} newPlan // new plan selected to subscribe
 * @param {*} newPackageName // new package name selected to subscribe (launch, convert, accelerate, enterprise)
 * @returns { key: string, isDowngradePackage: boolean }
 */
// Helper: handle trial or expired status logic (returns result object or null)
const evaluateTrialOrExpiredCase = (status, trialEndDate, hasNewGMVLimit) => {
  if (status !== TRIAL_STATUS && status !== EXPIRED_STATUS) return null;

  const dayLeft = getDayDiff(new Date(trialEndDate).getTime() / 1000);
  if (dayLeft <= 0) {
    return { key: 'TRIAL_ENDED', isDowngradePackage: false };
  }
  if (hasNewGMVLimit) {
    return { key: 'PRICE_CHANGE', isDowngradePackage: false };
  }
  return { key: 'TRIAL_NO_PRICE_CHANGE', isDowngradePackage: false };
};

// Helper: handle downgrade logic (returns result object or null)
const evaluateDowngradeCase = (newPlanGmvMax, gmvLimit, mainTier, newPackageName) => {
  if (newPlanGmvMax >= gmvLimit) return null;

  const oldTier = (mainTier || '').toLocaleLowerCase();
  const newTier = (newPackageName || '').toLocaleLowerCase();

  // Same package tier but lower GMV
  if (oldTier.includes(newTier)) {
    return { key: 'DOWNGRADE_PLAN', isDowngradePackage: false };
  }

  const downgradeMap = {
    [`${PACKAGE_NAME02}:${PACKAGE_NAME01}`]: 'PACKAGE_LEVEL02_TO_LEVEL01',
    [`${PACKAGE_NAME03}:${PACKAGE_NAME02}`]: 'PACKAGE_LEVEL03_TO_LEVEL02',
    [`${PACKAGE_NAME03}:${PACKAGE_NAME01}`]: 'PACKAGE_LEVEL03_TO_LEVEL01'
  };

  const mapKey = `${oldTier}:${newTier}`;
  if (downgradeMap[mapKey]) {
    return { key: downgradeMap[mapKey], isDowngradePackage: true };
  }

  return null;
};

export const getCorrectSubscribeCase = (invoice, newPlan, hasNewGMVLimit, tenantApp) => {
  const { trialEndDate } = invoice || {};
  const { status } = getCurrentPlanStatus(tenantApp);
  const gmvLimit = _.get(invoice, 'plan.gmvMax', 0);
  const mainTier = _.get(invoice, 'plan.mainTier', '');
  const newPackageName = _.get(newPlan, 'mainTier', '');
  const newPlanGmvMax = _.get(newPlan, 'gmvMax', 0);

  // Trial / expired pathway
  const trialResult = evaluateTrialOrExpiredCase(status, trialEndDate, hasNewGMVLimit);
  if (trialResult) return trialResult;

  // No price change
  if (newPlanGmvMax === gmvLimit) {
    return { key: 'PRICE_NO_CHANGE', isDowngradePackage: false };
  }

  // Downgrade logic
  const downgradeResult = evaluateDowngradeCase(newPlanGmvMax, gmvLimit, mainTier, newPackageName);
  if (downgradeResult) return downgradeResult;

  // Price change only (upgrade or lateral with GMV change)
  if (hasNewGMVLimit) {
    return { key: 'PRICE_CHANGE', isDowngradePackage: false };
  }

  // Upgrade
  return { key: 'UPGRADE_PLAN', isDowngradePackage: false };
};

export const isGMVIncompleteSync = baseGMV => {
  const { latestGMV = [] } = baseGMV || {};

  let hasZeroGMV = false;
  let hasPositiveGMV = false;
  for (const element of latestGMV) {
    const gmvValue = Object.values(element)[0];
    if (gmvValue === 0) hasZeroGMV = true;
    else if (gmvValue > 0) hasPositiveGMV = true;
    if (hasZeroGMV && hasPositiveGMV) return true;
  }
  return false;
};

export const getCurrentPlanStatus = tenantApp => {
  const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
  const trialExpired = _.get(tenantApp, 'trialExpired', false);
  const exceededPlan = _.get(tenantApp, 'exceededPlan', false);
  const chargeInfoActive = _.get(tenantApp, 'chargeInfoActive', {});
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const trialEndOn = _.get(chargeInfoActive, 'trial_ends_on', null);

  // only exceeded plan for bolt-on
  if (exceededPlan) {
    return {
      status: EXCEEDED_STATUS,
      dayLeft: 0
    };
  }
  // subscribe date is after trialEndOn 1 days
  // trialEndOn have format is YYYY-MM-DD
  const subscribedDate = trialEndOn
    ? new Date(new Date(trialEndOn).getTime() + 24 * 60 * 60 * 1000)
    : null;
  const dayLeft = getDayDiff(new Date(trialExpiresAt * 1000).getTime() / 1000);

  if (trialExpired) {
    return {
      status: EXPIRED_STATUS,
      dayLeft: 0
    };
  }

  if (dayLeft > 0 || _.isEmpty(selectedPlan)) {
    if (!_.isEmpty(selectedPlan)) {
      return {
        status: SUBSCRIBED_STATUS,
        subscribedDate: subscribedDate
          ? moment(subscribedDate).format(DATE_FORMAT.en.longDate)
          : null,
        dayLeft
      };
    }
    return {
      status: TRIAL_STATUS,
      dayLeft
    };
  }
  return {
    status: SUBSCRIBED_STATUS,
    dayLeft: 0
  };
};
